import { useEffect, useState } from "react";
import { Loader2, Landmark, Plus, Trash2, Save, RotateCcw, Printer } from "lucide-react";
import { toast } from "sonner";
import { api, apiError } from "@/lib/api";
import { fmtDateTime } from "@/lib/format";
import { PrinterSetup } from "@/components/docs/PrinterSetup";
import { getPrinter } from "@/lib/qz";

const blank = { bank: "", acc_number: "", holder: "" };

function AccountRow({ a, i, can, set, remove }) {
  return (
    <div className="bank-row" data-testid={`bank-row-${i}`}>
      <input className="rj-input" value={a.bank} disabled={!can} placeholder="Bank (mis. BCA)" onChange={(e) => set(i, { ...a, bank: e.target.value })} data-testid={`bank-name-${i}`} />
      <input className="rj-input" value={a.acc_number} disabled={!can} placeholder="Nomor rekening" inputMode="numeric" onChange={(e) => set(i, { ...a, acc_number: e.target.value })} data-testid={`bank-acc-${i}`} />
      <input className="rj-input" value={a.holder || ""} disabled={!can} placeholder="Atas nama (opsional)" onChange={(e) => set(i, { ...a, holder: e.target.value })} data-testid={`bank-holder-${i}`} />
      {can && <button className="rj-btn rj-btn-ghost rj-btn-sm" onClick={() => remove(i)} title="Hapus" data-testid={`bank-remove-${i}`}><Trash2 size={14} /></button>}
    </div>
  );
}

export function BankAccountsSettings({ onChanged }) {
  const [p, setP] = useState(null);
  const [rows, setRows] = useState([]);
  const [saving, setSaving] = useState(false);
  const apply = (d) => { setP(d); setRows(d.banks_source === "settings" ? d.banks.map((b) => ({ ...blank, ...b })) : []); };
  useEffect(() => { api.get("/finance/company-profile").then((r) => apply(r.data.data)).catch((e) => toast.error(apiError(e))); }, []);

  const save = async (accounts) => {
    setSaving(true);
    try {
      const r = await api.put("/finance/settings/bank-accounts", { accounts: accounts.map((a) => ({ bank: a.bank, acc_number: a.acc_number, holder: a.holder || null })) });
      apply(r.data.data); onChanged?.(); toast.success(accounts.length ? "Rekening pembayaran disimpan" : "Kembali memakai rekening dari Odoo");
    } catch (e) { toast.error(apiError(e)); } finally { setSaving(false); }
  };

  if (!p) return <div className="panel"><div className="rj-loading"><Loader2 className="spin" /> Memuat…</div></div>;
  const can = p.can_manage_banks;
  const set = (i, v) => setRows(rows.map((r, j) => (j === i ? v : r)));

  return (
    <div className="panel reminder-panel" data-testid="bank-accounts-settings">
      <div className="panel-head"><div><h3><Landmark size={16} style={{ verticalAlign: -3 }} /> Rekening Pembayaran</h3>
        <p>Dicetak di invoice & faktur serta dipakai variabel {"{rekening}"} di pengingat. Jika dikosongkan, memakai rekening dari Odoo.</p></div>
        <span className={`bill-chip ${p.banks_source === "settings" ? "merged" : "partial"}`} data-testid="bank-source">{p.banks_source === "settings" ? "Dari setting Finance" : "Dari Odoo"}</span></div>
      {p.banks_source === "odoo" && (
        <div className="bank-odoo" data-testid="bank-odoo-list"><small className="fin-muted">Rekening Odoo saat ini:</small>
          {(p.odoo_banks || []).length ? p.odoo_banks.map((b) => <span key={b.acc_number}>{b.bank} {b.acc_number} a.n. {b.holder || p.name}</span>) : <span>—</span>}</div>
      )}
      <div className="bank-rows">{rows.map((a, i) => <AccountRow key={i} a={a} i={i} can={can} set={set} remove={(j) => setRows(rows.filter((_, k) => k !== j))} />)}</div>
      {can ? (
        <div className="reminder-actions">
          {rows.length < 5 && <button className="rj-btn rj-btn-ghost rj-btn-sm" onClick={() => setRows([...rows, { ...blank, holder: p.name }])} data-testid="bank-add-btn"><Plus size={14} /> Tambah rekening</button>}
          <button className="rj-btn rj-btn-green rj-btn-sm" onClick={() => save(rows)} disabled={saving || !rows.length} data-testid="bank-save-btn">
            {saving ? <Loader2 size={14} className="spin" /> : <Save size={14} />} Simpan rekening</button>
          {p.banks_source === "settings" && <button className="rj-btn rj-btn-ghost rj-btn-sm" onClick={() => save([])} disabled={saving} data-testid="bank-reset-btn"><RotateCcw size={14} /> Pakai rekening Odoo</button>}
        </div>
      ) : <small className="fin-muted">Hanya Administrator / Finance Manager yang dapat mengubah rekening.</small>}
      {p.bank_accounts_updated_at && <p className="fin-muted" style={{ marginTop: 10 }}>Terakhir diubah {p.bank_accounts_updated_by} · {fmtDateTime(p.bank_accounts_updated_at)}</p>}
    </div>
  );
}

export function PrinterPanel() {
  const [open, setOpen] = useState(false);
  const [name, setName] = useState(getPrinter());
  return (
    <div className="panel reminder-panel" data-testid="printer-panel">
      <div className="panel-head"><div><h3><Printer size={16} style={{ verticalAlign: -3 }} /> Printer Dot-Matrix</h3>
        <p>SO, DO, Invoice & Kwitansi bisa di-print langsung (raw ESC/P) ke Epson LX-310 tanpa dialog, lewat aplikasi QZ Tray.</p></div></div>
      <div className="reminder-actions">
        <span className="fin-muted" data-testid="printer-current">Printer komputer ini: <b>{name || "belum dipilih"}</b></span>
        <button className="rj-btn rj-btn-primary rj-btn-sm" onClick={() => setOpen(true)} data-testid="printer-panel-open"><Printer size={14} /> Atur printer</button>
      </div>
      {open && <PrinterSetup onClose={() => { setOpen(false); setName(getPrinter()); }} />}
    </div>
  );
}
